import { PublicCertification } from '@wix/ambassador-academy-certifications-v1-account-certification/types';
import { Box } from '@wix/design-system';
import _ from 'lodash';
import React, { useMemo } from 'react';
import CompactCertifications from './CompactCertifications';
import DetailedCertifications from './DetailedCertifications';
import s from './Certifications.module.scss';
import { shouldRenderIndividualCertification } from './CertificationsUtils';
import {
  PROFILE_CERTIFICATIONS_CAROUSEL_BREAKPOINT,
  PROFILE_CERTIFICATIONS_LIMIT_SLIDE,
} from '@utils/constants';

interface CertificationsProps {
  isMobileView?: boolean;
  variant: 'detailed' | 'compact';
  data: PublicCertification[];
  onGradientStateChange?: (showLeft: boolean, showRight: boolean) => void;
}

const Certifications = ({
  isMobileView,
  variant,
  data: certificationList = [],
  onGradientStateChange,
}: CertificationsProps) => {
  const { certsToRender, shouldUseCarousel, maxSlideIndex } = useMemo(() => {
    const certsToRender = certificationList
      .sort((cert1, cert2) => {
        return (
          new Date(cert2.createdDate!).getTime() -
          new Date(cert1.createdDate!).getTime()
        );
      })
      .filter((cert) => shouldRenderIndividualCertification(cert));

    const shouldUseCarousel =
      certsToRender.length > PROFILE_CERTIFICATIONS_CAROUSEL_BREAKPOINT &&
      !isMobileView;
    const maxSlideIndex = Math.max(
      0,
      certsToRender.length - PROFILE_CERTIFICATIONS_LIMIT_SLIDE,
    );

    return {
      certsToRender,
      shouldUseCarousel,
      maxSlideIndex,
    };
  }, [certificationList, isMobileView]);

  if (_.isEmpty(certificationList)) {
    return null;
  }

  return (
    <Container
      isMobileView={isMobileView}
      className={
        variant === 'detailed' ? s.detailedContainer : s.compactContainer
      }
    >
      {variant === 'detailed' ? (
        <DetailedCertifications
          certifications={certsToRender}
          shouldUseCarousel={shouldUseCarousel}
          maxSlideIndex={maxSlideIndex}
          isMobileView={isMobileView}
          onGradientStateChange={onGradientStateChange}
        />
      ) : (
        <CompactCertifications
          certifications={certsToRender}
          isMobileView={isMobileView}
        />
      )}
    </Container>
  );
};

export default Certifications;

const Container = ({
  children,
  isMobileView,
  className,
}: {
  children: React.ReactNode;
  isMobileView?: boolean;
  className?: string;
}) => {
  return (
    <Box
      className={`${
        isMobileView ? s.mobileContainer : s.desktopContainer
      } ${className}`}
      height="fit-content"
      width="100%"
      direction="vertical"
      gap="12px"
      dataHook="certifications-container"
    >
      {children}
    </Box>
  );
};
